// src/utils/markdownUtils.js

/**
 * Extract front matter metadata from markdown content
 * Front matter is expected to be in YAML format between --- markers
 * 
 * @param {string} markdown - The markdown content with front matter
 * @returns {Object} - An object containing { metadata, content }
 */
export const extractFrontMatter = (markdown) => {
    const frontMatterRegex = /^---\s*([\s\S]*?)\s*---/;
    const match = frontMatterRegex.exec(markdown);
    
    if (!match) {
      return {
        metadata: {},
        content: markdown
      };
    }
    
    const frontMatter = match[1];
    const content = markdown.replace(frontMatterRegex, '').trim();
    const metadata = {};
    
    // Parse the front matter
    frontMatter.split('\n').forEach(line => {
      const [key, ...valueParts] = line.split(':');
      if (key && valueParts.length) {
        const value = valueParts.join(':').trim();
        // Handle arrays (comma-separated values)
        if (value.includes(',')) {
          metadata[key.trim()] = value.split(',').map(item => item.trim());
        } else {
          metadata[key.trim()] = value;
        }
      }
    });
    
    return { metadata, content };
  };
  
  /**
   * Extract the excerpt/summary from markdown content
   * 
   * @param {string} markdown - The markdown content
   * @param {number} words - Number of words to include in excerpt
   * @returns {string} - The excerpt
   */
  export const extractExcerpt = (markdown, words = 30) => {
    // Remove front matter if present
    const { content } = extractFrontMatter(markdown);
    
    // Remove markdown formatting
    const plainText = content
      .replace(/#{1,6}\s+/g, '') // Remove headings
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1') // Remove links
      .replace(/`(.*?)`/g, '$1') // Remove inline code
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/^\s*>[^\n]*/gm, '') // Remove blockquotes
      .replace(/^\s*- /gm, '') // Remove list items
      .replace(/^\s*\d+\. /gm, '') // Remove numbered list items
      .replace(/!\[(.*?)\]\((.*?)\)/g, ''); // Remove images
    
    // Get first n words
    const excerpt = plainText.trim().split(/\s+/).slice(0, words).join(' ');
    
    return excerpt + (plainText.split(/\s+/).length > words ? '...' : '');
  };
  
  /**
   * Generate a slug from a title
   * 
   * @param {string} title - The title to slugify
   * @returns {string} - The slug
   */
  export const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  };